import { PlatformSettingsFormShell } from '@app/modules/social-export/components/settings/PlatformSettingsFormShell';
import { BaseSettingsFormProps } from '@app/modules/social-export/hooks/social-export-form-props';
import { Grid } from '@mui/material';
import { z } from 'zod';
import {
  RudoPublishDtoRestrictionEnum,
  RudoSettingsDto,
  RudoSettingsDtoRestrictionEnumKey,
} from '../../social-export.types';
import { RudoFoldersField } from './folder-field/RudoFoldersField';
import { RudoRestrictionField } from './RudoRestrictionField';

/** Parses Autocomplete matchId folder value into a required folder id. */
function folderIdSchema() {
  return z
    .number({
      required_error: 'Required',
      invalid_type_error: 'Required',
    })
    .nullable()
    .refine((n): n is number => n != null && !Number.isNaN(n), { message: 'Required' });
}

/** Parses restriction select value into the API enum. */
function restrictionSchema() {
  return z
    .union([
      z.literal('0'),
      z.literal('1'),
      z.literal('2'),
      z.number(),
      z.literal(''),
      z.null(),
      z.undefined(),
    ])
    .transform((val) => {
      if (val === '' || val == null) return RudoPublishDtoRestrictionEnum['0'];
      const n = typeof val === 'number' ? val : Number(val);
      if (n === 1) return RudoPublishDtoRestrictionEnum['1'];
      if (n === 2) return RudoPublishDtoRestrictionEnum['2'];
      return RudoPublishDtoRestrictionEnum['0'];
    });
}

export const rudoSettingsSchema = z.object({
  folderId: folderIdSchema(),
  restriction: restrictionSchema(),
});

type RudoSettingsFormParsed = z.output<typeof rudoSettingsSchema>;

/**
 * Connection defaults for Rudo uploads: folder and VOD restriction.
 */
export function RudoSettingsForm({
  connectionId,
  onUpdate,
  defaultSettings,
}: BaseSettingsFormProps<RudoSettingsDto>) {
  const restriction =
    defaultSettings?.restriction == null ? '0' : String(defaultSettings.restriction);

  return (
    <PlatformSettingsFormShell
      values={{
        folderId: defaultSettings?.folderId ?? null,
        restriction,
      }}
      schema={rudoSettingsSchema}
      onSuccess={async (formData) => {
        const parsed = formData as unknown as RudoSettingsFormParsed;
        await onUpdate({
          folderId: parsed.folderId,
          restriction: parsed.restriction as RudoSettingsDtoRestrictionEnumKey,
        });
      }}
    >
      <Grid container>
        <Grid item md={6}>
          <RudoFoldersField connectionId={connectionId} required />
        </Grid>
      </Grid>
      <Grid container>
        <Grid item md={6}>
          <RudoRestrictionField />
        </Grid>
      </Grid>
    </PlatformSettingsFormShell>
  );
}
